import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SaveVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (label: string) => Promise<void>;
  isSaving?: boolean;
}

export function SaveVersionDialog({
  open,
  onOpenChange,
  onSave,
  isSaving = false,
}: SaveVersionDialogProps) {
  const [label, setLabel] = useState('');

  const handleSave = async () => {
    try {
      await onSave(label.trim());
      setLabel(''); // Reset after successful save
      onOpenChange(false);
    } catch (error) {
      // Error handling is done by parent component
      console.error('[SaveVersionDialog] Save error:', error);
    }
  };

  const handleCancel = () => {
    setLabel('');
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSaving) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-lg p-8 rounded-md">
        <DialogHeader>
          <DialogTitle>Save Version</DialogTitle>
          <DialogDescription>
            Create a snapshot of your case study that you can restore later.
            Give it a descriptive name to help identify it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="version-label">
              Version Label <span className="text-gray-500">(optional)</span>
            </Label>
            <Input
              id="version-label"
              placeholder="e.g., Before client feedback, Final draft, etc."
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSaving}
              maxLength={100}
              autoFocus
            />
            <p className="text-xs text-gray-500">
              Leave blank to save without a label
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
            className="w-40"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="w-40"
          >
            {isSaving ? 'Saving...' : 'Save Version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
